import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { Catalog } from "../../shared/types";

export const GAME_STORAGE_KEY = "pokefans.game";
/** Before the open games load (or if they can't): the site's first game. */
export const DEFAULT_GAME = "pokemon";

export interface GameValue {
  /** The game the visitor browses: the market, "Se busca" and the store's lists show only its cards. */
  game: string;
  /** The open games (those the switcher offers), in the site's order. */
  games: Catalog["games"];
  /** Switches and remembers the choice on this device. */
  setGame: (slug: string) => void;
}

const GameContext = createContext<GameValue>({ game: DEFAULT_GAME, games: [], setGame: () => {} });

function readStored(): string | null {
  try {
    return window.localStorage.getItem(GAME_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * The visitor's game (shared/games.ts): the one they chose last on this device while it's open, else the
 * first open game. Mounted by CatalogProvider, which knows the open games.
 */
export function GameProvider({ games, children }: { games: Catalog["games"]; children: ReactNode }) {
  const [chosen, setChosen] = useState<string | null>(readStored);

  const setGame = useCallback((slug: string) => {
    setChosen(slug);
    try {
      window.localStorage.setItem(GAME_STORAGE_KEY, slug);
    } catch {
      // Storage unavailable: the choice lasts for this visit.
    }
  }, []);

  const value = useMemo<GameValue>(() => {
    // A game that closed, or one never open: back to the first. Until the games load, the stored choice
    // goes as is (the server answers the first open game for one it doesn't know).
    const game = !games.length ? (chosen ?? DEFAULT_GAME) : games.some((g) => g.slug === chosen) ? chosen! : games[0].slug;
    return { game, games, setGame };
  }, [chosen, games, setGame]);

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame(): GameValue {
  return useContext(GameContext);
}
